import { useState } from 'react';
import { generatePath } from 'react-router-dom';
import { IconEdit, IconEye, IconTrash } from '@tabler/icons-react';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { AppTable, AppTableProps, RemoveModal } from 'components/organisms';
import { GroupConsultationDTO, GroupConsultationId } from 'types';
import { groupConsultationDTOFields } from 'consts/consultation/groupConsultation/groupConsultationDTOFields';
import {
    ConsultationChildrenDisplay,
    ConsultationParentsDisplay,
    ConsultationSubjectDisplay,
} from 'components/molecules';
import { Option } from 'components/atoms';
import { consultationTableAdditionalColumnsKeys } from 'consts/tables/consultation/consultationTableAdditionalColumnsKeys';
import { parseStringIntoFormattedStringDate } from 'utils/parsers/dateTime/parseStringIntoFormattedStringDate';
import { commonTexts, journalTexts } from 'consts/text/index';
import { appPaths } from 'consts/paths/paths';
import { useJournalAbilities } from 'abilities';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';
import { useGroupJournalConsultationsPageNetworkManage } from './hooks/useGroupJournalConsultationsPageNetworkManage';

const columns: AppTableProps<GroupConsultationDTO>['columns'] = [
    {
        title: journalTexts.journalDetails.common.consultations.consultationSubject,
        dataIndex: groupConsultationDTOFields.subject,
        key: groupConsultationDTOFields.subject,
        render: (_, consultation) => (
            <ConsultationSubjectDisplay
                subject={consultation.subject}
                date={consultation.date}
                journalId={consultation.journalId}
                consultationId={consultation.id}
                journalType="GROUP"
            />
        ),
    },
    {
        title: commonTexts.dataLabels.child,
        dataIndex: groupConsultationDTOFields.childrenWithParents,
        key: groupConsultationDTOFields.childrenWithParents,
        render: (_, consultation) => (
            <ConsultationChildrenDisplay
                consultationChildren={consultation[groupConsultationDTOFields.childrenWithParents]}
            />
        ),
        isVisibleAsExtendableInMobile: true,
    },
    {
        title: commonTexts.dataLabels.currentParent,
        dataIndex: consultationTableAdditionalColumnsKeys.parents,
        key: consultationTableAdditionalColumnsKeys.parents,
        render: (_, consultation) => (
            <ConsultationParentsDisplay
                consultationChildren={consultation[groupConsultationDTOFields.childrenWithParents]}
            />
        ),
        isVisibleAsExtendableInMobile: true,
    },
    {
        title: commonTexts.dataLabels.date,
        dataIndex: groupConsultationDTOFields.date,
        key: groupConsultationDTOFields.date,
        render: (date: GroupConsultationDTO['date']) => parseStringIntoFormattedStringDate(date),
        align: 'center',
        isInvisibleInMobile: true,
    },
];

export const GroupJournalConsultationsPage = () => {
    const { journalId } = useJournalId();
    const { statefulNavigate } = useNavigateWithState();
    const [selectedToRemoveConsultationId, setSelectedToRemoveConsultationId] =
        useState<GroupConsultationId | null>(null);
    const {
        groupJournalsAbilities: { checkIfCanManageConsultations },
    } = useJournalAbilities();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'GROUP',
    });

    const canManageConsultations = checkIfCanManageConsultations();

    const onConsultationRemoveRequestFinishOrCancel = () => setSelectedToRemoveConsultationId(null);

    const {
        consultationsData,
        isConsultationsDataRefetching,
        isConsultationsDataError,
        isRemoveConsultationLoading,
        isConsultationsDataInitialLoading,
        removeConsultation,
    } = useGroupJournalConsultationsPageNetworkManage({
        journalId,
        onConsultationRemoveRequestFinish: onConsultationRemoveRequestFinishOrCancel,
    });

    const navigateToConsultationAddPage = () => {
        statefulNavigate(
            generatePath(
                appPaths.app.journals.groupJournalDetails.pickedJournal.consultations.add,
                { journalId },
            ),
        );
    };

    const navigateToConsultationEditPage = ({ id }: GroupConsultationDTO) => {
        statefulNavigate(
            generatePath(
                appPaths.app.journals.groupJournalDetails.pickedJournal.consultations.edit
                    .pickedConsultation,
                {
                    journalId,
                    consultationId: id,
                },
            ),
        );
    };

    const navigateToConsultationDetailsPage = ({ id }: GroupConsultationDTO) => {
        statefulNavigate(
            generatePath(
                appPaths.app.journals.groupJournalDetails.pickedJournal.consultations.details
                    .pickedConsultation,
                {
                    journalId,
                    consultationId: id,
                },
            ),
        );
    };

    const handleRemoveConsultationClick = (consultation: GroupConsultationDTO) =>
        setSelectedToRemoveConsultationId(consultation.id);

    const handleRemoveConsultationConfirm = () => {
        if (!selectedToRemoveConsultationId) {
            return;
        }
        removeConsultation({
            journalId,
            consultationId: selectedToRemoveConsultationId,
        });
    };

    const options: Option<GroupConsultationDTO>[] = [
        {
            label: commonTexts.actionLabels.details,
            onClick: navigateToConsultationDetailsPage,
            Icon: <IconEye />,
        },
        {
            label: commonTexts.actionLabels.edit,
            onClick: navigateToConsultationEditPage,
            Icon: <IconEdit />,
            checkIfVisible: () => canManageConsultations,
        },
        {
            label: commonTexts.actionLabels.delete,
            onClick: handleRemoveConsultationClick,
            Icon: <IconTrash />,
            checkIfVisible: () => canManageConsultations,
        },
    ];

    return (
        <>
            <RemoveModal
                open={!!selectedToRemoveConsultationId}
                title={journalTexts.journalDetails.common.consultations.confirmDeleteConsultation}
                confirmActionLoading={isRemoveConsultationLoading}
                onOk={handleRemoveConsultationConfirm}
                onCancel={onConsultationRemoveRequestFinishOrCancel}
            />
            <AppTable
                title={journalTexts.journalDetails.common.consultations.consultationList}
                addButtonLabel={journalTexts.journalDetails.common.consultations.addConsultation}
                emptyInfo={journalTexts.journalDetails.common.consultations.emptyConsultationList}
                options={options}
                columns={columns}
                dataSource={consultationsData}
                rowKey={({ id }) => id}
                isTableManageUtilsHidden
                isError={isConsultationsDataError}
                isOptionsLoading={isRemoveConsultationLoading}
                isDataInitialLoading={isConsultationsDataInitialLoading}
                isDataRefetching={isConsultationsDataRefetching}
                isHiddenAddButton={!canManageConsultations}
                onAddButtonClick={navigateToConsultationAddPage}
            />
        </>
    );
};
